import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  BookOpen, 
  Check, 
  ArrowRight, 
  Sparkles,
  Building2,
  CreditCard,
  Zap,
  Shield,
  RefreshCw,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const isMyBookieDomain = () => {
  const hostname = window.location.hostname;
  return !hostname.includes('repti-sitter') && !hostname.includes('reptisitter');
};

const getHomePath = () => isMyBookieDomain() ? '/' : '/mybookie';
const getDashboardPath = () => isMyBookieDomain() ? '/dashboard' : '/mybookie/dashboard';

const MyBookiePricing = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loadingPlan, setLoadingPlan] = useState(null);

  // httpOnly cookie auth - check session with /auth/me
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentTier, setCurrentTier] = useState('trial');

  useState(() => {
    axios.get(`${API}/api/mybookie/auth/me`, { withCredentials: true })
      .then(res => {
        setIsLoggedIn(true);
        setCurrentTier(res.data.subscription_status || 'trial');
      })
      .catch(() => setIsLoggedIn(false));
  });

  // Inject FAQPage structured data so Google can show a rich FAQ snippet for the
  // /pricing page (improves AI search & rich result eligibility).
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Can I switch plans anytime?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! Upgrade or downgrade whenever you want. Changes take effect immediately."
          }
        },
        {
          "@type": "Question",
          "name": "Is there a free trial?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "All paid plans include a 14-day free trial. No credit card required to start."
          }
        },
        {
          "@type": "Question",
          "name": "Can I import my data from QuickBooks?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! Business plan and above includes our QuickBooks import tool. Export your QBO data and import it in minutes."
          }
        },
        {
          "@type": "Question",
          "name": "What happens after my trial ends?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You'll be automatically moved to the Free plan—no charges. Your data stays safe."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer refunds?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, 30-day money-back guarantee on all paid plans. No questions asked."
          }
        },
        {
          "@type": "Question",
          "name": "Which plan includes CFO Mode (runway, board pack, AI Finance Brief)?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CFO Mode is included with the Business plan ($39/mo) and Business+ plan ($59/mo). Business+ adds advanced features: scenario planning, AI commentary, auto-generated Board Pack PDFs, and weekly AI Finance Brief emails."
          }
        },
        {
          "@type": "Question",
          "name": "How does MyBookie compare to QuickBooks?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "MyBookie is up to 80% cheaper than QuickBooks Plus ($99/mo), with built-in CFO insights, anomaly detection, and AI-powered auto-categorization that QuickBooks doesn't offer at any price."
          }
        }
      ]
    };
    const id = 'mybookie-pricing-faq-schema';
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(faqSchema);
    return () => {
      // Remove on unmount so the schema doesn't bleed into other pages
      const stale = document.getElementById(id);
      if (stale) stale.remove();
    };
  }, []);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      tagline: 'Perfect for getting started',
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        '1 business',
        'Manual expense entry',
        'Basic P&L reports',
        'FinanceBot AI (10 questions/mo)',
        'CSV export',
        'Mobile friendly'
      ],
      limitations: [
        'No bank connection',
        'No CFO Insights',
        'No invoicing'
      ],
      cta: 'Start Free',
      popular: false
    },
    {
      id: 'pro',
      name: 'Solo',
      tagline: 'For solopreneurs & side hustlers',
      monthlyPrice: 19,
      yearlyPrice: 190,
      features: [
        'Unlimited businesses',
        'Bank connection (Plaid · 12,000+ banks)',
        'Smart auto-categorization (vendor learning)',
        'Bulk re-categorize tool',
        'Receipt scanning & mileage tracking',
        'Cash flow forecasting',
        'Multi-currency P&L reports',
        'FinanceBot AI (unlimited)'
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      id: 'business',
      name: 'Business',
      tagline: 'For growing businesses · CFO mode unlocked',
      monthlyPrice: 39,
      yearlyPrice: 390,
      features: [
        'Everything in Solo',
        'CFO Insights: Runway & Burn Rate',
        'Budget vs Actuals tracking',
        'Anomaly Detection (duplicates, spikes)',
        'Invoicing with automated reminders',
        'W-2 & 1099-NEC tax forms',
        'Client Portal & accountant access',
        'Priority email support'
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      id: 'business_plus',
      name: 'Business+',
      tagline: 'For teams with payroll & advanced needs',
      monthlyPrice: 59,
      yearlyPrice: 590,
      features: [
        'Everything in Business (including full CFO Insights)',
        'Advanced CFO: Scenario Planning & AI Commentary',
        'Auto-generated Board Pack PDFs',
        'Weekly AI Finance Brief emails',
        'Full-service Payroll (progressive state brackets, paystubs)',
        'Multi-user access & team roles',
        'API access',
        'Advanced compliance reports (SOC 2, NACHA)',
        'Real-time security alert emails',
        'Custom reports & advanced exports',
        'Dedicated support'
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: false
    }
  ];

  const competitors = [
    { name: 'QuickBooks Plus', price: '$99/mo', note: 'No anomaly detection or CFO insights' },
    { name: 'Nume AI CFO', price: '$200+/mo', note: 'No bookkeeping, accountant-grade only' },
    { name: 'FreshBooks Premium', price: '$60/mo', note: 'Limited reporting, no payroll' },
    { name: 'Xero Established', price: '$78/mo', note: 'No CFO insights or anomaly detection' }
  ];

  const handleSelectPlan = async (plan) => {
    // Not logged in — bounce to landing with auto-open signup and a return path
    if (!isLoggedIn) {
      const planParam = plan.id !== 'free' ? `&plan=${plan.id}` : '';
      navigate(`${getHomePath()}?signup=1&from=pricing${planParam}`);
      return;
    }

    // Free plan — logged in user wants to downgrade
    if (plan.id === 'free') {
      navigate(getDashboardPath());
      return;
    }

    // Already on this plan
    if (currentTier === plan.id) {
      navigate(getDashboardPath());
      return;
    }

    // Trigger Stripe checkout
    setLoadingPlan(plan.id);
    try {
      const response = await axios.post(
        `${API}/api/mybookie/stripe/create-checkout`,
        {
          package_id: plan.id,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      setLoadingPlan(null);
    }
  };

  const getButtonLabel = (plan) => {
    if (loadingPlan === plan.id) return 'Redirecting...';
    if (!isLoggedIn) return plan.id === 'free' ? 'Start Free' : 'Get Started';
    if (plan.id === 'free' && ['trial', 'free'].includes(currentTier)) return 'Current Plan';
    if (currentTier === plan.id) return 'Current Plan';
    if (plan.id === 'free') return 'Downgrade';
    return `Upgrade to ${plan.name}`;
  };

  const isCurrentPlan = (plan) => {
    if (!isLoggedIn) return false;
    if (plan.id === 'free' && ['trial', 'free'].includes(currentTier)) return true;
    return currentTier === plan.id;
  };

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      {/* Header */}
      <nav className="bg-white border-b border-[#E6E8E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => navigate(getHomePath())}
          >
            <div className="w-10 h-10 bg-[#0A2B1D] rounded-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-[#111614] tracking-tight">MyBookie</span>
          </div>
          {isLoggedIn ? (
            <Button 
              onClick={() => navigate(getDashboardPath())}
              className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
            >
              Dashboard
            </Button>
          ) : (
            <Button 
              onClick={() => navigate(`${getHomePath()}?signup=1&from=pricing`)}
              className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
            >
              Get Started
            </Button>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-medium text-[#111614] mb-4 tracking-tight">
          Simple pricing. No surprises.
        </h1>
        <p className="text-lg text-[#5C6661] max-w-2xl mx-auto mb-8">
          Up to 80% cheaper than QuickBooks. No price jumps after promos. 
          No per-user fees. No client limits. Ever.
        </p>

        {/* Billing Toggle */}
        <div className="inline-flex items-center gap-3 bg-white p-1 rounded-sm border border-[#E6E8E6]">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              billingCycle === 'monthly' 
                ? 'bg-[#0A2B1D] text-white' 
                : 'text-[#5C6661] hover:text-[#111614]'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              billingCycle === 'yearly' 
                ? 'bg-[#0A2B1D] text-white' 
                : 'text-[#5C6661] hover:text-[#111614]'
            }`}
          >
            Yearly <span className="text-[#3A6E53]">(Save 17%)</span>
          </button>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.name}
              data-testid={`pricing-card-${plan.id}`}
              className={`relative overflow-hidden border-2 ${
                plan.popular 
                  ? 'border-[#0A2B1D] shadow-xl scale-105' 
                  : 'border-[#E6E8E6]'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-[#0A2B1D] text-white text-xs py-1 text-center font-medium">
                  MOST POPULAR
                </div>
              )}
              <CardContent className={`p-6 ${plan.popular ? 'pt-10' : ''}`}>
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-[#111614]">{plan.name}</h3>
                  <p className="text-sm text-[#5C6661]">{plan.tagline}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-[#111614]">
                      ${billingCycle === 'monthly' ? plan.monthlyPrice : Math.round(plan.yearlyPrice / 12)}
                    </span>
                    <span className="text-[#5C6661]">/mo</span>
                  </div>
                  {billingCycle === 'yearly' && plan.yearlyPrice > 0 && (
                    <p className="text-xs text-[#5C6661] mt-1">
                      ${plan.yearlyPrice}/year billed annually
                    </p>
                  )}
                  {plan.monthlyPrice === 0 && (
                    <p className="text-xs text-[#3A6E53] font-medium mt-1">
                      Free forever
                    </p>
                  )}
                </div>

                <Button 
                  data-testid={`select-plan-${plan.id}`}
                  disabled={isCurrentPlan(plan) || loadingPlan === plan.id}
                  className={`w-full mb-6 rounded-sm ${
                    isCurrentPlan(plan)
                      ? 'bg-[#E2ECE7] text-[#5C6661] cursor-default'
                      : plan.popular 
                        ? 'bg-[#0A2B1D] hover:bg-[#254D3D] text-white' 
                        : 'bg-white border-2 border-[#0A2B1D] text-[#0A2B1D] hover:bg-[#E2ECE7]'
                  }`}
                  onClick={() => handleSelectPlan(plan)}
                >
                  {loadingPlan === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {getButtonLabel(plan)} 
                  {!isCurrentPlan(plan) && loadingPlan !== plan.id && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>

                <div className="space-y-3">
                  {plan.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#3A6E53] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#111614]">{feature}</span>
                    </div>
                  ))}
                  {plan.limitations.map((limitation) => (
                    <div key={limitation} className="flex items-start gap-2 opacity-50">
                      <span className="w-4 h-4 text-center text-[#8A948F] mt-0.5 flex-shrink-0">-</span>
                      <span className="text-sm text-[#8A948F]">{limitation}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Competitor Comparison */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold text-[#111614] text-center mb-4">
            See how we compare
          </h2>
          <p className="text-[#5C6661] text-center mb-8">
            MyBookie Solo starts at $19/mo — and gives you more than competitors charging 2-5x the price.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {competitors.map((comp) => (
              <div key={comp.name} className="flex items-center justify-between p-4 bg-[#F6F5F2] rounded-sm">
                <div>
                  <p className="font-medium text-[#111614]">{comp.name}</p>
                  <p className="text-xs text-[#8A948F]">{comp.note}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-[#A33B31] line-through">{comp.price}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-[#E2ECE7] rounded-sm text-center">
            <p className="text-lg font-medium text-[#0A2B1D]">
              MyBookie Solo: <span className="text-2xl font-bold">$19/mo</span>
            </p>
            <p className="text-sm text-[#5C6661] mt-1">
              Unlimited businesses · Bank connection · AI insights · No limits
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-semibold text-[#111614] text-center mb-12">
            Every plan includes
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#E2ECE7] rounded-sm flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-[#0A2B1D]" />
              </div>
              <h3 className="font-medium text-[#111614] mb-2">Bank-Level Security</h3>
              <p className="text-sm text-[#5C6661]">256-bit encryption. SOC 2 compliant. Your data is safe.</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#E2ECE7] rounded-sm flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-[#0A2B1D]" />
              </div>
              <h3 className="font-medium text-[#111614] mb-2">AI-Powered Insights</h3>
              <p className="text-sm text-[#5C6661]">FinanceBot answers your questions in plain English.</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#E2ECE7] rounded-sm flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-[#0A2B1D]" />
              </div>
              <h3 className="font-medium text-[#111614] mb-2">5-Minute Setup</h3>
              <p className="text-sm text-[#5C6661]">No accountant needed. If you can use Instagram, you can use MyBookie.</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#E2ECE7] rounded-sm flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-6 h-6 text-[#0A2B1D]" />
              </div>
              <h3 className="font-medium text-[#111614] mb-2">Multi-Business</h3>
              <p className="text-sm text-[#5C6661]">Track side hustles, rentals, and consulting—all in one place.</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#E2ECE7] rounded-sm flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-6 h-6 text-[#0A2B1D]" />
              </div>
              <h3 className="font-medium text-[#111614] mb-2">No Hidden Fees</h3>
              <p className="text-sm text-[#5C6661]">The price you see is the price you pay. Period.</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#E2ECE7] rounded-sm flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-6 h-6 text-[#0A2B1D]" />
              </div>
              <h3 className="font-medium text-[#111614] mb-2">Auto-Categorization</h3>
              <p className="text-sm text-[#5C6661]">Transactions sorted instantly by AI. No manual tagging required.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-semibold text-[#111614] text-center mb-8">
            Common questions
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-medium text-[#111614] mb-2">Can I switch plans anytime?</h3>
              <p className="text-sm text-[#5C6661]">Yes! Upgrade or downgrade whenever you want. Changes take effect immediately.</p>
            </div>
            <div>
              <h3 className="font-medium text-[#111614] mb-2">Is there a free trial?</h3>
              <p className="text-sm text-[#5C6661]">All paid plans include a 14-day free trial. No credit card required to start.</p>
            </div>
            <div>
              <h3 className="font-medium text-[#111614] mb-2">Can I import my data from QuickBooks?</h3>
              <p className="text-sm text-[#5C6661]">Yes! Business plan and above includes our QuickBooks import tool. Export your QBO data and import it in minutes.</p>
            </div>
            <div>
              <h3 className="font-medium text-[#111614] mb-2">What happens after my trial ends?</h3>
              <p className="text-sm text-[#5C6661]">You'll be automatically moved to the Free plan—no charges. Your data stays safe.</p>
            </div>
            <div>
              <h3 className="font-medium text-[#111614] mb-2">Do you offer refunds?</h3>
              <p className="text-sm text-[#5C6661]">Yes, 30-day money-back guarantee on all paid plans. No questions asked.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#0A2B1D]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-medium text-white mb-4">
            Ready to simplify your books?
          </h2>
          <p className="text-[#B4C9BE] mb-8">
            Join thousands of entrepreneurs who ditched the complexity.
          </p>
          <Button 
            size="lg"
            className="bg-white text-[#0A2B1D] hover:bg-[#E2ECE7] rounded-sm px-8"
            onClick={() => {
              if (isLoggedIn) {
                navigate(getDashboardPath());
              } else {
                navigate(`${getHomePath()}?signup=1&from=pricing`);
              }
            }}
          >
            Start Free <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111614] text-[#8A948F] py-8 px-6">
        <div className="max-w-5xl mx-auto text-center text-sm">
          &copy; 2026 MyBookie. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default MyBookiePricing;
